import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatINR } from "@/lib/api";
import SalesLinesDialog from "@/components/SalesLinesDialog";
import MultiSelect from "@/components/MultiSelect";
import IndiaSalesMap from "@/components/IndiaSalesMap";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, FileSpreadsheet, Search, Hourglass, BookOpenText, ReceiptText, TrendingUp, TrendingDown, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const input = "bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50";
const TABS = [["overview", "Overview"], ["explorer", "Explorer"], ["customers", "Customers"], ["products", "Products"], ["plants", "Plants"], ["geo", "Geography"]];
const ORDERS = [
  ["customer,product,plant", "Customer → Product → Plant"],
  ["customer,plant,product", "Customer → Plant → Product"],
  ["product,customer,plant", "Product → Customer → Plant"],
  ["product,plant,customer", "Product → Plant → Customer"],
  ["plant,product,customer", "Plant → Product → Customer"],
  ["plant,customer,product", "Plant → Customer → Product"],
];
const DIM_PARAM = { customer: "customer", product: "material", plant: "plant" };

const Kpi = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testid}>
    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-lg font-bold text-slate-900 mt-0.5">{value}</p>
    {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
  </div>
);

function PivotRows({ nodes, depth, pathFilters, open, toggle, onDrill, total }) {
  return nodes.flatMap((n) => {
    const key = pathFilters.map((f) => f.key).join("|") + `|${n.dim}:${n.key}`;
    const nextFilters = [...pathFilters, { dim: n.dim, key: n.key, label: n.label }];
    const isOpen = !!open[key];
    const hasKids = !!(n.children && n.children.length);
    const row = (
      <tr key={key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pivot-row-${depth}-${n.key}`}>
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-1" style={{ paddingLeft: depth * 22 }}>
            {hasKids ? (
              <button onClick={() => toggle(key)} className="text-slate-400 hover:text-slate-700" data-testid={`pivot-toggle-${depth}-${n.key}`}>
                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
            ) : <span className="w-[13px]" />}
            <span className={`truncate max-w-[340px] ${depth === 0 ? "font-medium text-slate-800" : "text-slate-600"}`}>
              {n.dim === "product" && n.key !== n.label ? <><span className="font-mono text-[11.5px]">{n.key}</span> · {n.label}</> : n.label}
            </span>
          </div>
        </td>
        <td className="px-3 py-1.5 text-right">{n.invoices}</td>
        <td className="px-3 py-1.5 text-right font-mono">{(n.qty || 0).toLocaleString("en-IN")}</td>
        <td className="px-3 py-1.5 text-right font-mono">{formatINR(n.sales)}</td>
        <td className="px-3 py-1.5 text-right text-slate-500">{total ? ((n.sales / total) * 100).toFixed(1) : "0.0"}%</td>
        <td className="px-2 py-1.5 text-right">
          <button onClick={() => onDrill(nextFilters)} data-testid={`pivot-inv-${depth}-${n.key}`}
            className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5 ml-auto">
            <ReceiptText size={11} /> Invoices
          </button>
        </td>
      </tr>
    );
    if (!isOpen || !hasKids) return [row];
    return [row, ...PivotRows({ nodes: n.children, depth: depth + 1, pathFilters: nextFilters, open, toggle, onDrill, total })];
  });
}

export default function SalesPerformancePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [drill, setDrill] = useState(null);
  const [exporting, setExporting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [fromD, setFromD] = useState(monthStart);
  const [toD, setToD] = useState(today);
  const [selCust, setSelCust] = useState([]);
  const [selProd, setSelProd] = useState([]);
  const [order, setOrder] = useState(ORDERS[0][0]);
  const [pivot, setPivot] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [rangeNote, setRangeNote] = useState("");
  const [openNodes, setOpenNodes] = useState({});
  const [geoSel, setGeoSel] = useState("");
  const touched = useRef(false);

  useEffect(() => {
    api.get("/reports/sales-performance").then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (tab !== "explorer" || !data) return;
    let dead = false;
    setPivotLoading(true);
    const p = new URLSearchParams({ order });
    if (fromD) p.set("from_date", fromD);
    if (toD) p.set("to_date", toD);
    if (selCust.length) p.set("customer", selCust.join(","));
    if (selProd.length) p.set("material", selProd.join(","));
    api.get(`/reports/sales-performance/pivot?${p}`).then(({ data: d }) => {
      if (dead) return;
      if (d.count === 0 && !touched.current && (fromD || toD)) {
        setRangeNote("No invoices in the current month-to-date — showing all available data instead.");
        setFromD(""); setToD("");
        return;
      }
      setPivot(d);
      setOpenNodes({});
      setPivotLoading(false);
    }).catch((e) => { if (!dead) { toast.error(e.response?.data?.detail || e.message); setPivotLoading(false); } });
    return () => { dead = true; };
  }, [tab, data, order, fromD, toD, selCust, selProd]);

  const custOptions = useMemo(() => (data?.customers || []).map((c) => ({ value: c.customer_code, label: `${c.customer_name} (${c.customer_code})` })), [data]);
  const prodOptions = useMemo(() => (data?.products || []).map((p) => ({ value: p.material, label: `${p.material} — ${p.description || ""}` })), [data]);

  const openPivotDrill = (filters) => {
    const f = { from_date: fromD || undefined, to_date: toD || undefined };
    if (selCust.length) f.customer = selCust.join(",");
    if (selProd.length) f.material = selProd.join(",");
    filters.forEach((x) => { f[DIM_PARAM[x.dim]] = x.key; });
    setDrill({ ...f, title: `Invoices — ${filters.map((x) => x.label).join(" › ")}` });
  };

  const customers = useMemo(() => {
    const q = search.toLowerCase();
    return (data?.customers || []).filter((r) => !q || r.customer_name.toLowerCase().includes(q) || r.customer_code.toLowerCase().includes(q));
  }, [data, search]);

  const products = useMemo(() => {
    const q = search.toLowerCase();
    return (data?.products || []).filter((r) => !q || r.material.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [data, search]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/sales-performance/export`, { credentials: "include" });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "DeePiping_sales-performance.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } finally { setExporting(false); }
  };

  if (error) return <div className="p-8 text-sm text-red-600" data-testid="sales-perf-error">{error}</div>;
  if (!data) return <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const k = data.kpis || {};

  return (
    <div className="space-y-4 p-6" data-testid="sales-perf-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Sales Performance</h1>
          <p className="text-sm text-slate-500">Billing documents · {data.lines_total?.toLocaleString("en-IN")} invoice lines · as of {data.as_of}</p>
        </div>
        <button onClick={exportExcel} disabled={exporting} data-testid="sales-perf-export-excel-button"
          className="flex items-center gap-1.5 text-xs bg-emerald-700/80 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-md px-3 py-1.5 transition-colors">
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel (all lines)
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="sales-perf-kpis">
        <Kpi label="Total Sales" value={formatINR(k.total_sales)} testid="sales-kpi-total" />
        <Kpi label="Invoices" value={(k.invoice_count || 0).toLocaleString("en-IN")} sub={`avg ${formatINR(k.avg_invoice)}`} testid="sales-kpi-invoices" />
        <Kpi label="Customers" value={k.customer_count || 0} sub={`top share ${k.top_customer_share || 0}%`} testid="sales-kpi-customers" />
        <Kpi label="Latest Month Growth" testid="sales-kpi-growth"
          value={k.latest_month_growth == null ? "—" : (
            <span className={`flex items-center gap-1 ${k.latest_month_growth >= 0 ? "text-emerald-700" : "text-red-600"}`}>
              {k.latest_month_growth >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}{k.latest_month_growth}%
            </span>)} />
        <Kpi label="AR Outstanding" value={formatINR(k.total_outstanding)} testid="sales-kpi-outstanding" />
        <Kpi label="DSO" value={k.dso_days == null ? "—" : `${k.dso_days} days`} sub="collection days" testid="sales-kpi-dso" />
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="sales-perf-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); setSearch(""); }} data-testid={`sales-tab-${key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-[#B01E24] text-[#B01E24]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="sales-monthly-chart">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Monthly Sales Trend</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.monthly} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 10000000).toFixed(1)}Cr`} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="sales" fill="#B01E24" radius={[4, 4, 0, 0]} cursor="pointer"
                  onClick={(d) => d?.month_key && setDrill({ month: d.month_key, title: `Invoices — ${d.month}` })} />
              </BarChart>
            </ResponsiveContainer>
            <table className="w-full text-[12.5px] mt-3" data-testid="sales-monthly-table">
              <thead><tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-2 py-1.5">Month</th><th className="text-right px-2 py-1.5">Sales</th>
                <th className="text-right px-2 py-1.5">Invoices</th><th className="text-right px-2 py-1.5">Growth</th></tr></thead>
              <tbody>
                {data.monthly.map((m) => (
                  <tr key={m.month_key} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setDrill({ month: m.month_key, title: `Invoices — ${m.month}` })} data-testid={`sales-month-${m.month_key}`}>
                    <td className="px-2 py-1.5">{m.month}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatINR(m.sales)}</td>
                    <td className="px-2 py-1.5 text-right">{m.invoices}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${m.growth_pct == null ? "text-slate-400" : m.growth_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {m.growth_pct == null ? "—" : `${m.growth_pct > 0 ? "+" : ""}${m.growth_pct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="sales-plant-split">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Plant Split</h3>
              {data.plants.map((p) => (
                <div key={p.plant} className="mb-2.5">
                  <div className="flex justify-between text-[12px]"><span>{p.plant}</span><span className="font-mono">{formatINR(p.sales)} · {p.share_pct}%</span></div>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-1.5 bg-[#B01E24]/70 rounded-full" style={{ width: `${Math.min(p.share_pct, 100)}%` }} /></div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="sales-top5-customers">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Top 5 Customers</h3>
              {(data.customers || []).slice(0, 5).map((c) => (
                <div key={c.customer_code} className="flex justify-between text-[12px] py-1 border-b border-slate-100 last:border-0">
                  <span className="truncate pr-2">{c.customer_name}</span><span className="font-mono whitespace-nowrap">{formatINR(c.sales)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(tab === "customers" || tab === "products") && (
        <div className="relative max-w-xs">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${tab}…`}
            data-testid={`sales-${tab}-search`} className={`${input} pl-8 w-full`} />
        </div>
      )}

      {tab === "customers" && data.name_lookup && data.name_lookup.found === 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="sales-names-note">
          Customer names could not be read from the SAP customer master (A_Customer / A_BusinessPartner) — codes are shown instead.
          Please check the SAP user's Business Partner master-data authorization.
        </div>
      )}
      {tab === "customers" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto thin-scroll" data-testid="sales-customers-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50"><tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Invoices</th><th className="text-right px-3 py-2">Sales</th>
              <th className="text-right px-3 py-2">Share</th><th className="text-right px-3 py-2">Outstanding</th>
              <th className="text-right px-3 py-2">DSO</th><th className="text-left px-3 py-2">Last Invoice</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.customer_code} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sales-customer-${c.customer_code}`}>
                  <td className="px-3 py-2 font-mono text-[11.5px] text-slate-500">{c.customer_code}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.customer_name}</td>
                  <td className="px-3 py-2 text-right">{c.invoices}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatINR(c.sales)}</td>
                  <td className="px-3 py-2 text-right">{c.share_pct}%</td>
                  <td className="px-3 py-2 text-right font-mono">{c.outstanding ? formatINR(c.outstanding) : "—"}</td>
                  <td className="px-3 py-2 text-right">{c.dso_days == null ? "—" : `${c.dso_days}d`}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.last_invoice}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => setDrill({ customer: c.customer_code, title: `Invoices — ${c.customer_name}` })}
                        data-testid={`sales-cust-inv-${c.customer_code}`}
                        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5">
                        <ReceiptText size={11} /> Invoices
                      </button>
                      <button onClick={() => navigate(`/reports/ar-ageing?q=${encodeURIComponent(c.customer_name)}`)}
                        data-testid={`sales-cust-ageing-${c.customer_code}`}
                        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5">
                        <Hourglass size={11} /> Ageing
                      </button>
                      <button onClick={() => navigate(`/reports/ar-ledger?q=${encodeURIComponent(c.customer_name)}`)}
                        data-testid={`sales-cust-ledger-${c.customer_code}`}
                        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5">
                        <BookOpenText size={11} /> Ledger
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "products" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto thin-scroll" data-testid="sales-products-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50"><tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Material</th><th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Sales</th>
              <th className="text-right px-3 py-2">Share</th><th className="text-right px-3 py-2">Invoices</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.material} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sales-product-${p.material}`}>
                  <td className="px-3 py-2 font-mono text-[11.5px]">{p.material}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[280px] truncate">{p.description}</td>
                  <td className="px-3 py-2 text-right font-mono">{(p.qty || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatINR(p.sales)}</td>
                  <td className="px-3 py-2 text-right">{p.share_pct}%</td>
                  <td className="px-3 py-2 text-right">{p.invoices}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => setDrill({ material: p.material, title: `Invoices — ${p.material}` })}
                      data-testid={`sales-prod-inv-${p.material}`}
                      className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5 ml-auto">
                      <ReceiptText size={11} /> Invoices
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "plants" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto thin-scroll max-w-2xl" data-testid="sales-plants-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50"><tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Plant</th><th className="text-right px-3 py-2">Sales</th><th className="text-right px-3 py-2">Share</th></tr></thead>
            <tbody>
              {data.plants.map((p) => (
                <tr key={p.plant} className="border-b border-slate-100" data-testid={`sales-plant-${p.plant}`}>
                  <td className="px-3 py-2">{p.plant}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatINR(p.sales)}</td>
                  <td className="px-3 py-2 text-right">{p.share_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "explorer" && (
        <div className="space-y-3" data-testid="sales-explorer">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</span>
              <input type="date" value={fromD} max={toD || today} onChange={(e) => { touched.current = true; setFromD(e.target.value); }}
                data-testid="explorer-from-date" className={input} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</span>
              <input type="date" value={toD} max={today} onChange={(e) => { touched.current = true; setToD(e.target.value); }}
                data-testid="explorer-to-date" className={input} />
            </div>
            <MultiSelect label="Customers" options={custOptions} values={selCust} onChange={setSelCust} testid="explorer-customers-select" />
            <MultiSelect label="Products" options={prodOptions} values={selProd} onChange={setSelProd} testid="explorer-products-select" />
            <select value={order} onChange={(e) => setOrder(e.target.value)} data-testid="explorer-order-select" className={input}>
              {ORDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {rangeNote && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="explorer-range-note">{rangeNote}</div>}
          {pivotLoading && <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>}
          {!pivotLoading && pivot && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-auto thin-scroll" data-testid="explorer-pivot-table">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-3 py-2">{ORDERS.find(([v]) => v === order)?.[1]}</th>
                    <th className="text-right px-3 py-2">Invoices</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Sales</th>
                    <th className="text-right px-3 py-2">Share</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {PivotRows({ nodes: pivot.tree, depth: 0, pathFilters: [], open: openNodes,
                    toggle: (k) => setOpenNodes((o) => ({ ...o, [k]: !o[k] })), onDrill: openPivotDrill, total: pivot.totals.sales })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t border-slate-200 font-semibold">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right">{pivot.totals.invoices}</td>
                    <td className="px-3 py-2 text-right font-mono">{(pivot.totals.qty || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono" data-testid="explorer-total-sales">{formatINR(pivot.totals.sales)}</td>
                    <td className="px-3 py-2 text-right">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              {!pivot.tree.length && <p className="text-sm text-slate-500 p-6 text-center">No invoices match the selected filters.</p>}
            </div>
          )}
        </div>
      )}

      {tab === "geo" && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="sales-geo">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Sales Coverage Map</h3>
            <p className="text-[11px] text-slate-500 mb-2">Customer-state wise billing · darker = higher sales · click a state for its customers</p>
            <IndiaSalesMap geo={data.geo || []} selected={geoSel} onSelect={setGeoSel} />
            {data.geo_unmapped > 0 && (
              <p className="text-[11px] text-amber-700 mt-2" data-testid="geo-unmapped-note">
                {formatINR(data.geo_unmapped)} of sales is from customers without a state (no region/GSTIN in SAP master).
              </p>
            )}
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-auto thin-scroll" data-testid="geo-state-table">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50"><tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">State</th><th className="text-right px-3 py-2">Invoices</th>
                  <th className="text-right px-3 py-2">Sales</th><th className="text-right px-3 py-2">Share</th></tr></thead>
                <tbody>
                  {(data.geo || []).map((g) => (
                    <tr key={g.state_code} onClick={() => setGeoSel(geoSel === g.state_code ? "" : g.state_code)}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${geoSel === g.state_code ? "bg-red-50/60" : ""}`}
                      data-testid={`geo-state-${g.state_code}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{g.state}</td>
                      <td className="px-3 py-2 text-right">{g.invoices}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(g.sales)}</td>
                      <td className="px-3 py-2 text-right">{g.share_pct}%</td>
                    </tr>
                  ))}
                  {!(data.geo || []).length && <tr><td colSpan={4} className="p-6 text-center text-sm text-slate-500">Customer locations unavailable from SAP.</td></tr>}
                </tbody>
              </table>
            </div>
            {geoSel && (
              <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="geo-state-customers">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">
                  Customers — {(data.geo || []).find((g) => g.state_code === geoSel)?.state}
                </h3>
                {((data.geo || []).find((g) => g.state_code === geoSel)?.customers || []).map((c) => (
                  <div key={c.customer_code} className="flex items-center justify-between text-[12px] py-1 border-b border-slate-100 last:border-0">
                    <span className="truncate pr-2">{c.customer_name} <span className="font-mono text-[10.5px] text-slate-400">{c.customer_code}</span></span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <span className="font-mono">{formatINR(c.sales)}</span>
                      <button onClick={() => setDrill({ customer: c.customer_code, title: `Invoices — ${c.customer_name}` })}
                        data-testid={`geo-cust-inv-${c.customer_code}`}
                        className="text-[10.5px] text-slate-500 hover:text-[#B01E24] border border-slate-200 rounded px-1.5 py-0.5">Invoices</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <SalesLinesDialog open={!!drill} onClose={() => setDrill(null)} filter={drill} />
    </div>
  );
}
